export class ApiClientError extends Error {
  // Phase 4 Task 14: `details` carries the raw parsed error body beyond {code,message} — needed by
  // BattleController/ArenaController's controller-local @ExceptionHandlers, which (per their own
  // javadoc) return richer shapes than the shared GlobalExceptionHandler's two-field ApiError:
  // battle_active's `activeBattleId` (BattleController.BattleActiveBody/ArenaController's own copy)
  // and requirement_unmet's `firstUnmet` (BattleController.RequirementUnmetBody). Optional and
  // untyped here — every other caller ignores it — so this is additive, not a breaking change to
  // the constructor's existing two call sites in this file.
  constructor(public code: string, message: string, public status: number, public details?: Record<string, unknown>) { super(message); }
}

/** The app's query retry policy (App.tsx's `QueryClient`). TanStack's default — three tries,
 *  backing off — kept a closed stall's URL on its skeleton for ~7 s before the 404 was allowed to
 *  show (QA 2026-09-23, economy F6). A 404 is an answer, not a hiccup: never retried. Everything
 *  else keeps the default budget. */
export function retryUnlessNotFound(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiClientError && error.status === 404) return false;
  return failureCount < 3;
}

const cookie = (name: string) =>
  document.cookie.split('; ').find(c => c.startsWith(name + '='))?.split('=')[1];

async function ensureCsrf(): Promise<string> {
  const existing = cookie('XSRF-TOKEN');
  if (existing) return decodeURIComponent(existing);
  const res = await fetch('/api/v1/csrf', { credentials: 'same-origin' });
  if (!res.ok) throw new ApiClientError('csrf_unavailable', 'Could not obtain a security token.', res.status);
  const { token } = await res.json();
  return token;
}

/**
 * Global session-expiry signal (fix-wave item 10). App.tsx registers a handler that navigates to
 * /login with the same `{ reason: 'session-expired' }` location state MeScreen's own dedicated
 * 401 handling already uses (LoginScreen renders a note for it) — so every "your session ended"
 * path in the app looks identical to the player, not just the one MeScreen happened to cover.
 *
 * Exported as a setter, not a hard import of a router/navigate function, so this module stays
 * framework-agnostic and independently testable (a test registers its own spy the same way
 * App.tsx registers the real navigate call).
 */
type UnauthorizedHandler = () => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

/**
 * Phase 6 Task 15: Decision #10's grandfather-flow latch, wired via the exact same seam shape as
 * `setUnauthorizedHandler` above — a module-level handler set once from App.tsx, fired here
 * whenever `/me` resolves successfully, rather than a hard import of a zustand store (keeps this
 * module framework-agnostic and independently testable, same reasoning the handler above's own
 * comment gives). `mustRename` is read straight off the raw `/me` response body (AuthController's
 * `AccountView.mustRename`) — this is the ONLY call site that inspects a response body's business
 * fields rather than treating `apiFetch<T>` as a pure passthrough, justified because `/me` is
 * itself a special-cased path already (see `NEVER_TRIGGERS_UNAUTHORIZED`/`meSucceededBefore`
 * above).
 */
type MustRenameHandler = (mustRename: boolean) => void;
let mustRenameHandler: MustRenameHandler | null = null;
export function setMustRenameHandler(handler: MustRenameHandler | null): void {
  mustRenameHandler = handler;
}

// Paths that must NEVER trigger the handler, even on a 401/403: the CSRF bootstrap fetch, every
// auth/* call (a login/register 401 is that screen's own "bad credentials" flow, not a session
// expiry — logout's response is irrelevant either way), and /me itself (both an anonymous
// visitor's very first, expected-to-401 bootstrap check, AND MeScreen's own pre-existing
// dedicated 401 handling for that exact call — this global handler firing too would just be a
// redundant second navigate to the same place).
const NEVER_TRIGGERS_UNAUTHORIZED = [/^\/auth\//, /^\/csrf$/, /^\/me$/];

// The other half of the "don't loop on first load" guard: fire the handler only once a session
// is KNOWN to have existed in this tab (a successful /me), never on a bare 401/403 with no prior
// evidence of one — otherwise an anonymous visitor's very first /companions probe (or any other
// authenticated-area call made before /me has ever succeeded) would bounce straight to /login
// with a "session expired" note that's simply false; they were never logged in to begin with.
let meSucceededBefore = false;

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);
  headers.set('Content-Type', 'application/json');
  if (method !== 'GET') headers.set('X-XSRF-TOKEN', await ensureCsrf());
  const res = await fetch(`/api/v1${path}`, { ...init, method, headers, credentials: 'same-origin' });
  if (!res.ok) {
    let code = 'unknown', message = `Request failed (${res.status})`, details: Record<string, unknown> | undefined;
    try { const body = await res.json(); code = body.code ?? code; message = body.message ?? message; details = body; } catch { /* non-JSON error */ }
    const exempt = NEVER_TRIGGERS_UNAUTHORIZED.some((p) => p.test(path));
    if ((res.status === 401 || res.status === 403) && !exempt && meSucceededBefore) {
      unauthorizedHandler?.();
    }
    throw new ApiClientError(code, message, res.status, details);
  }
  if (path === '/me') {
    meSucceededBefore = true;
    const body = (await res.json()) as T;
    const mustRename = (body as unknown as { mustRename?: unknown })?.mustRename;
    if (typeof mustRename === 'boolean') mustRenameHandler?.(mustRename);
    return body;
  }
  // Phase 6 Task 21 boundary fix (a REAL product defect, found by the new e2e spec, invisible to
  // every MSW-based unit test because those all mock a literal `HttpResponse(null, {status:204})`
  // for a void endpoint's success case — the REAL server's actual behaviour for a bare `void`
  // Spring MVC handler method with no `@ResponseStatus`/`ResponseEntity` override is 200 OK with
  // an EMPTY body, not 204 (confirmed against the real running server: `POST /api/v1/wishlists`
  // returns 200 with a zero-byte body; so does `GET /api/v1/guilds/mine` for a guildless account —
  // `GuildController.mine()`'s own `.orElse(null)` ALSO serializes to a genuinely empty body, not
  // the literal text "null", since Spring's message converter skips writing a body entirely for a
  // null return value). The old `res.status === 204` special-case above never caught either shape
  // — this function fell through to a bare `res.json()` on an EMPTY string, which throws a
  // SyntaxError, silently turning a SUCCESSFUL call into a REJECTED promise. Every caller built on
  // `useMutation`'s hook-level `onSuccess: () => invalidateQueries(...)` idiom (wishlists.ts,
  // friends.ts's decline/cancel/remove/block/unfollow, guilds.ts's invite/leave/kick/disband/bank
  // ops, moderation.ts's report/hide/unhide/appeal, and more — 25 bare-`void` controller methods
  // across the codebase) silently never re-rendered its own screen after a real, successful
  // action, recoverable only by a manual reload; every caller relying on a `T | null` GET (like
  // `getMyGuild(): Promise<GuildDetailDto | null>`) threw instead of resolving the real, meaningful
  // "no guild yet" `null` — breaking `/guild`'s own guildless-redirect for every guildless account,
  // ever, in every environment (an EVEN OLDER, more severe latent bug this same fix closes).
  // Fixed at this ONE choke point (every API call in the app already routes through here) rather
  // than touching 25+ individual server controllers: treat ANY empty response body as a real, safe
  // `null` — never `undefined`, which TanStack Query v5 treats as "no update" for a queryFn's
  // return and would have left `useQuery`'s own `data`/`isSuccess` never resolving correctly for a
  // query whose honest answer is "nothing" — covering 204 and both 200-with-empty-body shapes alike.
  const text = await res.text();
  if (!text) return null as T;
  return JSON.parse(text) as T;
}
